/**
 * SystemIndicator - Displays the current test environment and build version.
 *
 * Shows a small badge in the bottom-right corner so developers and QA can
 * tell at a glance which worktree they are looking at and which commit is
 * running. The port → label map matches the post-2026-04 worktree scheme
 * documented in CLAUDE.md.
 *
 * VH-84:
 *   • New port→label map (master/vh-dev-1/vh-dev-2/fh-dev-1/fh-dev-2/sit)
 *   • Renders `branch@sha` underneath the env label (Vite-injected)
 *   • Hidden on the production hostname only (visible on UAT + local dev)
 */

const SYSTEM_NAMES = {
  3000: 'master',
  3001: 'vh-dev-1',
  3002: 'vh-dev-2',
  3003: 'fh-dev-1',
  3004: 'fh-dev-2',
  3100: 'sit',
}

const SYSTEM_COLORS = {
  3000: 'bg-gray-100 text-gray-800 border-gray-300',
  3001: 'bg-red-100 text-red-800 border-red-300',
  3002: 'bg-blue-100 text-blue-800 border-blue-300',
  3003: 'bg-green-100 text-green-800 border-green-300',
  3004: 'bg-purple-100 text-purple-800 border-purple-300',
  3100: 'bg-yellow-100 text-yellow-800 border-yellow-300',
}

// Hostnames where the badge MUST be hidden (end-user production).
const PRODUCTION_HOSTNAMES = new Set([
  'vitahubco.com',
  'www.vitahubco.com',
])

/**
 * Decide whether the badge should render. Default-permissive: hide only on
 * the known production hostnames. Local dev, UAT, and any unknown host
 * (preview deploy, staging) all show the badge.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function shouldShowIndicator(hostname) {
  if (!hostname) return true
  return !PRODUCTION_HOSTNAMES.has(hostname.toLowerCase())
}

/**
 * Build the `branch@sha` string. Returns an empty string when either piece
 * is missing so the caller can simply skip the second line.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function formatBuildVersion(branch, sha) {
  if (!branch || !sha) return ''
  return `${branch}@${sha}`
}

export default function SystemIndicator() {
  const port = parseInt(import.meta.env.VITE_PORT || window.location.port || '5173')
  const systemName = SYSTEM_NAMES[port] || `port-${port}`
  const colorClasses = SYSTEM_COLORS[port] || 'bg-gray-100 text-gray-800 border-gray-300'

  if (!shouldShowIndicator(window.location.hostname)) {
    return null
  }

  const branch = import.meta.env.VITE_BUILD_BRANCH || ''
  const sha = import.meta.env.VITE_BUILD_SHA || ''
  const buildVersion = formatBuildVersion(branch, sha)

  const tooltip = buildVersion
    ? `Test Environment: ${systemName} (port ${port}) — build ${buildVersion}`
    : `Test Environment: ${systemName} (port ${port})`

  return (
    <div
      data-testid="system-indicator"
      className={`fixed bottom-4 right-4 z-50 px-3 py-1.5 text-xs font-mono font-semibold rounded-lg border shadow-sm leading-tight ${colorClasses}`}
      title={tooltip}
    >
      <div>{systemName}</div>
      {buildVersion && (
        <div
          data-testid="system-indicator-build"
          className="text-[10px] font-normal opacity-80 mt-0.5"
        >
          {buildVersion}
        </div>
      )}
    </div>
  )
}
